import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Percent, Plus, Pencil, Trash2, Loader2, Tag, Calendar } from "lucide-react";
import { toast } from "sonner";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const EMPTY = { codice: "", descrizione: "", tipo: "percentuale", percentuale: 0, importo_fisso: 0, min_giorni: 0, min_importo: 0, data_inizio: "", data_fine: "", max_utilizzi: 0, max_per_cliente: 0, categorie_ids: [], automatico: false, attivo: true };

const STATO = {
  attiva: { label: "Attiva", cls: "bg-[#39FF14]/15 text-[#39FF14]" },
  non_ancora_valida: { label: "Non ancora valida", cls: "bg-[#f98125]/15 text-[#f98125]" },
  scaduta: { label: "Scaduta", cls: "bg-zinc-700/40 text-zinc-400" },
  disattivata: { label: "Disattivata", cls: "bg-[#FF3B30]/15 text-[#FF3B30]" },
};
const FILTERS = [["tutte", "Tutte"], ["attiva", "Attive"], ["non_ancora_valida", "Programmate"], ["scaduta", "Scadute"], ["disattivata", "Disattivate"]];

const NumField = ({ label, value, onChange, testid }) => (
  <div><Label className="text-xs uppercase tracking-wider text-zinc-500">{label}</Label>
    <Input type="number" data-testid={testid} value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
);

export default function ScontoCampaigns() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutte");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get("/pricing/discounts"), api.get("/categories")]);
      setItems(d.data.filter((x) => !x.automatico || (x.codice || "").trim())); setCategories(c.data);
    } catch (e) { toast.error("Errore di caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => filter === "tutte" ? items : items.filter((d) => d.stato === filter), [items, filter]);
  const counts = useMemo(() => items.reduce((a, d) => { a[d.stato] = (a[d.stato] || 0) + 1; return a; }, {}), [items]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...EMPTY, ...d }); setOpen(true); };
  const toggleCat = (id) => setForm((f) => ({ ...f, categorie_ids: (f.categorie_ids || []).includes(id) ? f.categorie_ids.filter((x) => x !== id) : [...(f.categorie_ids || []), id] }));

  const save = async () => {
    if (!form.codice.trim()) { toast.error("Il codice promozionale è obbligatorio"); return; }
    if (form.tipo === "percentuale" && (form.percentuale <= 0 || form.percentuale > 100)) { toast.error("Percentuale sconto non valida (1-100)"); return; }
    setBusy(true);
    try {
      const p = { ...form, codice: form.codice.trim() };
      if (editing) await api.put(`/pricing/discounts/${editing.id}`, p);
      else await api.post("/pricing/discounts", p);
      toast.success("Campagna salvata"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare la campagna "${d.codice}"?`)) return;
    try { await api.delete(`/pricing/discounts/${d.id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="space-y-5" data-testid="sconto-campaigns">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-zinc-500 text-sm">Campagne sconto con codice promozionale. Lo stato viene calcolato dalle date di validità.</p>
        <button onClick={openNew} data-testid="camp-new-btn" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2.5 text-sm hover:bg-[#d96a12]"><Plus className="h-4 w-4" /> Nuova campagna</button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="camp-filters">
        {FILTERS.map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`camp-filter-${k}`}
            className={`text-xs uppercase tracking-wide px-3 py-1.5 rounded-sm border transition-colors ${filter === k ? "border-[#f98125] bg-[#f98125]/10 text-[#f98125]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            {lbl}{k !== "tutte" && counts[k] ? ` (${counts[k]})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#f98125]" /></div>
      ) : shown.length === 0 ? (
        <div className="text-zinc-600 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-sm">Nessuna campagna in questo stato.</div>
      ) : (
        <div className="space-y-2" data-testid="camp-list">
          {shown.map((d) => (
            <div key={d.id} data-testid={`camp-row-${d.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-2xl text-[#f98125]">{d.tipo === "fisso" ? eur(d.importo_fisso) : `${d.percentuale}%`}</span>
                  <span className="text-xs bg-zinc-800 px-2 py-0.5 rounded-sm font-mono flex items-center gap-1"><Tag className="h-3 w-3" />{d.codice}</span>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${STATO[d.stato]?.cls}`}>{STATO[d.stato]?.label || d.stato}</span>
                </div>
                {d.descrizione && <div className="text-xs text-zinc-500 mt-1">{d.descrizione}</div>}
                <div className="text-[11px] text-zinc-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{d.data_inizio || "…"} → {d.data_fine || "…"}</span>
                  {d.min_giorni > 0 && <span>da {d.min_giorni}gg</span>}
                  {d.min_importo > 0 && <span>min {eur(d.min_importo)}</span>}
                  {d.max_utilizzi > 0 ? <span>usi {d.utilizzi_totali ?? 0}/{d.max_utilizzi}</span> : (d.utilizzi_totali ?? 0) > 0 && <span>usi {d.utilizzi_totali}</span>}
                  {d.max_per_cliente > 0 && <span>max {d.max_per_cliente}/cliente</span>}
                  {(d.categorie_ids || []).length > 0 && <span>{d.categorie_ids.length} categorie</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(d)} data-testid={`camp-edit-${d.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(d)} data-testid={`camp-del-${d.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg" data-testid="camp-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Percent className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica campagna" : "Nuova campagna sconto"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Codice promozionale *</Label><Input data-testid="camp-codice" value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm font-mono" placeholder="ESTATE10" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="camp-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800">
                    <SelectItem value="percentuale">Percentuale %</SelectItem>
                    <SelectItem value="fisso">Importo fisso €</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.tipo === "fisso"
                ? <NumField label="Importo sconto €" testid="camp-fisso" value={form.importo_fisso} onChange={(v) => setForm({ ...form, importo_fisso: v })} />
                : <NumField label="Sconto percentuale %" testid="camp-perc" value={form.percentuale} onChange={(v) => setForm({ ...form, percentuale: v })} />}
              <NumField label="Importo minimo noleggio €" testid="camp-minimporto" value={form.min_importo} onChange={(v) => setForm({ ...form, min_importo: v })} />
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Descrizione sconto</Label><Input data-testid="camp-desc" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="Es. Promo estate -10%" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Valida dal</Label><Input type="date" data-testid="camp-dal" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Valida al</Label><Input type="date" data-testid="camp-al" value={form.data_fine} onChange={(e) => setForm({ ...form, data_fine: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <NumField label="Min. giorni" testid="camp-mingiorni" value={form.min_giorni} onChange={(v) => setForm({ ...form, min_giorni: v })} />
              <NumField label="Max utilizzi (0=∞)" testid="camp-maxutilizzi" value={form.max_utilizzi} onChange={(v) => setForm({ ...form, max_utilizzi: v })} />
              <NumField label="Max per cliente" testid="camp-maxcliente" value={form.max_per_cliente} onChange={(v) => setForm({ ...form, max_per_cliente: v })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Categorie ammesse (vuoto = tutte)</Label>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((c) => (
                  <label key={c.id} data-testid={`camp-cat-${c.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-sm border cursor-pointer text-sm ${(form.categorie_ids || []).includes(c.id) ? "border-[#f98125] bg-[#f98125]/10" : "border-zinc-800"}`}>
                    <Checkbox checked={(form.categorie_ids || []).includes(c.id)} onCheckedChange={() => toggleCat(c.id)} />{c.nome}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><Switch checked={form.attivo} onCheckedChange={(v) => setForm({ ...form, attivo: v })} data-testid="camp-attivo" /> <span className="text-sm">Campagna attiva</span></label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} disabled={busy} data-testid="camp-save" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
